import React, { useState, useEffect } from 'react';
import { __ } from './translations';
import { Send, Users, CheckCircle, AlertCircle, Loader, Bold, Italic, Type, Clock, RefreshCw, Link, ExternalLink } from 'lucide-react';

interface EmailProgress {
    total: number;
    sent: number;
    failed: number;
    remaining: number;
    in_progress: boolean;
    start_time: string;
    subject: string;
    percent_complete: number;
    estimated_completion?: string;
    time_elapsed?: string;
    completion_time?: string;
    recent_details?: Array<{
        email: string;
        status: 'success' | 'failed';
        message: string;
        timestamp: string;
    }>;
}

const CustomEmailSender: React.FC = () => {
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [selectedRecipients, setSelectedRecipients] = useState({
        regular: false,
        supporter: false
    });
    const [sending, setSending] = useState(false);
    const [emailProgress, setEmailProgress] = useState<EmailProgress | null>(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [recipientCount, setRecipientCount] = useState(0);
    const [loadingCount, setLoadingCount] = useState(false);
    const [statusInterval, setStatusInterval] = useState<NodeJS.Timeout | null>(null);
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [linkText, setLinkText] = useState('');
    const [linkUrl, setLinkUrl] = useState('');
    const [testEmail, setTestEmail] = useState('');
    const [sendingTest, setSendingTest] = useState(false);

    // Get recipient count when selection changes
    useEffect(() => {
        getRecipientCount();
    }, [selectedRecipients]);

    // Check for existing batch process on component mount
    useEffect(() => {
        checkExistingBatch();
        setEmailProgress(null);
        return () => {
            if (statusInterval) {
                clearInterval(statusInterval);
            }
        };
    }, []);

    const checkExistingBatch = async () => {
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/custom-email-status`, {
                headers: {
                    'X-WP-Nonce': boardMemberData.nonce
                }
            });

            const result = await response.json();
            if (result.success && result.progress) {
                setEmailProgress(result.progress);
                if (result.progress.in_progress) {
                    startStatusPolling();
                }
            }
        } catch (error) {
            console.error('Error checking existing batch:', error);
        }
    };

    const startStatusPolling = () => {
        if (statusInterval) {
            clearInterval(statusInterval);
        }

        const interval = setInterval(async () => {
            try {
                const response = await fetch(`${boardMemberData.restURL}board-member/v1/custom-email-status`, {
                    headers: {
                        'X-WP-Nonce': boardMemberData.nonce
                    }
                });

                const result = await response.json();
                if (result.success && result.progress) {
                    setEmailProgress(result.progress);

                    if (!result.progress.in_progress) {
                        clearInterval(interval);
                        setStatusInterval(null);
                        setSending(false);
                    }
                }
            } catch (error) {
                console.error('Error polling status:', error);
            }
        }, 2000); // Poll every 2 seconds

        setStatusInterval(interval);
    };

    const getRecipientCount = async () => {
        if (!selectedRecipients.regular && !selectedRecipients.supporter) {
            setRecipientCount(0);
            return;
        }

        setLoadingCount(true);
        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/recipient-count`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce
                },
                body: JSON.stringify({
                    recipients: selectedRecipients
                })
            });

            const result = await response.json();
            if (result.success) {
                setRecipientCount(result.count);
            }
        } catch (error) {
            console.error('Error getting recipient count:', error);
            setRecipientCount(0);
        } finally {
            setLoadingCount(false);
        }
    };

    const handleRecipientChange = (type: 'regular' | 'supporter') => {
        setSelectedRecipients(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    // Text formatting functions
    const insertFormatting = (tag: string) => {
        const textarea = document.getElementById('email-content') as HTMLTextAreaElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const selectedText = content.substring(start, end);

        let newText = '';
        switch (tag) {
            case 'bold':
                newText = `<strong>${selectedText}</strong>`;
                break;
            case 'italic':
                newText = `<em>${selectedText}</em>`;
                break;
            case 'br':
                newText = selectedText + '<br>';
                break;
            case 'p':
                newText = `<p>${selectedText}</p>`;
                break;
            case 'link':
                setShowLinkDialog(true);
                setLinkText(selectedText);
                return;
            default:
                newText = selectedText;
        }

        const newContent = content.substring(0, start) + newText + content.substring(end);
        setContent(newContent);

        // Reset cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + newText.length, start + newText.length);
        }, 0);
    };

    const insertLink = () => {
        if (!linkUrl.trim()) {
            alert(__('Please enter a URL'));
            return;
        }

        const textarea = document.getElementById('email-content') as HTMLTextAreaElement;
        if (!textarea) return;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const displayText = linkText.trim() || linkUrl;
        const linkHtml = `<a href="${linkUrl}" target="_blank">${displayText}</a>`;

        const newContent = content.substring(0, start) + linkHtml + content.substring(end);
        setContent(newContent);

        // Reset dialog
        setShowLinkDialog(false);
        setLinkText('');
        setLinkUrl('');

        // Reset cursor position
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(start + linkHtml.length, start + linkHtml.length);
        }, 0);
    };

    const validateForm = (testSend: boolean) => {
        if (!subject.trim()) {
            alert(__('Please enter an email subject'));
            return false;
        }
        if (!content.trim()) {
            alert(__('Please enter email content'));
            return false;
        }
        if (!testSend && !selectedRecipients.regular && !selectedRecipients.supporter) {
            alert(__('Please select at least one recipient group'));
            return false;
        }
        return true;
    };

    const sendCustomEmail = async () => {
        if (!validateForm()) return;

        // Check if there's already a batch in progress
        if (emailProgress && emailProgress.in_progress) {
            alert(__('A batch email process is already running. Please wait for it to complete.'));
            return;
        }

        setSending(true);
        setEmailProgress(null);

        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/send-custom-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce
                },
                body: JSON.stringify({
                    subject,
                    content,
                    recipients: selectedRecipients
                })
            });

            const result = await response.json();

            if (result.success) {
                // Clear form on success
                setSubject('');
                setContent('');
                setSelectedRecipients({ regular: false, supporter: false });

                // Start polling for status
                startStatusPolling();

                // Show initial status
                setEmailProgress({
                    total: result.total_recipients,
                    sent: 0,
                    failed: 0,
                    remaining: result.total_recipients,
                    in_progress: true,
                    start_time: new Date().toISOString(),
                    subject,
                    percent_complete: 0
                });
            } else {
                alert(result.message || __('Failed to start email sending process'));
                setSending(false);
            }
        } catch (error) {
            console.error('Error starting custom email batch:', error);
            alert(__('An error occurred while starting the email process'));
            setSending(false);
        }
    };

    const refreshStatus = () => {
        checkExistingBatch();
    };

    const sendTestEmail = async () => {
        if (!validateForm(true)) return;

        if (!testEmail.trim() || !isValidEmail(testEmail)) {
            alert(__('Please enter a valid test email address'));
            return;
        }

        setSendingTest(true);

        try {
            const response = await fetch(`${boardMemberData.restURL}board-member/v1/send-test-email`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': boardMemberData.nonce
                },
                body: JSON.stringify({
                    subject,
                    content,
                    test_email: testEmail
                })
            });

            const result = await response.json();

            if (result.success) {
                alert(__('Test email sent successfully to ') + testEmail);
            } else {
                alert(result.message || __('Failed to send test email'));
            }
        } catch (error) {
            console.error('Error sending test email:', error);
            alert(__('An error occurred while sending the test email'));
        } finally {
            setSendingTest(false);
        }
    };

    // Add this helper function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white shadow rounded-lg">
                <div className="border-b border-gray-200 px-6 py-4">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <Send className="h-5 w-5 text-gray-500" />
                        {__('Send Custom Email to Members')}
                    </h2>
                </div>

                <div className="px-6 py-4 space-y-6">
                    {/* Subject Line */}
                    <div>
                        <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-2">
                            {__('Email Subject')}
                        </label>
                        <input
                            type="text"
                            id="email-subject"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder={__('Enter email subject...')}
                            disabled={sending || (emailProgress && emailProgress.in_progress)}
                        />
                    </div>

                    {/* Recipient Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {__('Select Recipients')}
                        </label>
                        <div className="space-y-2">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedRecipients.regular}
                                    onChange={() => handleRecipientChange('regular')}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                    {__('Regular Members')}
                                </span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={selectedRecipients.supporter}
                                    onChange={() => handleRecipientChange('supporter')}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                />
                                <span className="ml-2 text-sm text-gray-700">
                                    {__('Supporters')}
                                </span>
                            </label>
                        </div>

                        {/* Recipient Count Display */}
                        <div className="mt-2 p-2 bg-blue-50 rounded-md">
                            <div className="flex items-center text-sm text-blue-800">
                                <Users className="h-4 w-4 mr-1" />
                                {loadingCount ? (
                                    <span>{__('Calculating recipients...')}</span>
                                ) : (
                                    <span>
                                        {recipientCount > 0
                                            ? __('Selected recipients: %d users').replace('%d', recipientCount.toString())
                                            : __('No recipients selected')
                                        }
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Email Content */}
                    <div>
                        <label htmlFor="email-content" className="block text-sm font-medium text-gray-700 mb-2">
                            {__('Email Content')}
                        </label>

                        {/* Formatting Toolbar */}
                        <div className="mb-2 border border-gray-300 rounded-t-md bg-gray-50 px-3 py-2">
                            <div className="flex items-center space-x-2">
                                <button
                                    type="button"
                                    onClick={() => insertFormatting('bold')}
                                    className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                    title={__('Bold')}
                                >
                                    <Bold className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormatting('italic')}
                                    className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                    title={__('Italic')}
                                >
                                    <Italic className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormatting('br')}
                                    className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                    title={__('Line Break')}
                                >
                                    ↵
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormatting('p')}
                                    className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                    title={__('Paragraph')}
                                >
                                    <Type className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => insertFormatting('link')}
                                    className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    disabled={sending || (emailProgress && emailProgress.in_progress)}
                                    title={__('Insert Link')}
                                >
                                    <Link className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="email-content"
                            rows={12}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-b-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            placeholder={__('Enter your email content here...')}
                            disabled={sending || (emailProgress && emailProgress.in_progress)}
                        />
                        <p className="mt-1 text-xs text-gray-500">
                            {__('Use the formatting buttons above to add HTML formatting. The email will be styled with IGWP branding automatically.')}
                        </p>
                    </div>

                    {/* Link Dialog */}
                    {showLinkDialog && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg p-6 w-full max-w-md">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                                        <ExternalLink className="h-5 w-5" />
                                        {__('Insert Link')}
                                    </h3>
                                    <button
                                        onClick={() => setShowLinkDialog(false)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label htmlFor="link-text" className="block text-sm font-medium text-gray-700 mb-1">
                                            {__('Link Text')}
                                        </label>
                                        <input
                                            type="text"
                                            id="link-text"
                                            value={linkText}
                                            onChange={(e) => setLinkText(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder={__('Text to display')}
                                        />
                                    </div>

                                    <div>
                                        <label htmlFor="link-url" className="block text-sm font-medium text-gray-700 mb-1">
                                            {__('URL')}
                                        </label>
                                        <input
                                            type="url"
                                            id="link-url"
                                            value={linkUrl}
                                            onChange={(e) => setLinkUrl(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                            placeholder="https://example.com"
                                        />
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 mt-6">
                                    <button
                                        onClick={() => setShowLinkDialog(false)}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {__('Cancel')}
                                    </button>
                                    <button
                                        onClick={insertLink}
                                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {__('Insert Link')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview Toggle */}
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={() => setPreviewMode(!previewMode)}
                            className="text-sm text-blue-600 hover:text-blue-800 underline"
                            disabled={sending || (emailProgress && emailProgress.in_progress)}
                        >
                            {previewMode ? __('Hide Preview') : __('Show Preview')}
                        </button>
                    </div>

                    {/* Preview */}
                    {previewMode && (
                        <div className="border-t pt-4">
                            <h3 className="text-sm font-medium text-gray-700 mb-2">{__('Email Preview')}</h3>
                            <div className="bg-gray-50 p-4 rounded border">
                                <div className="bg-white p-4 rounded shadow-sm">
                                    <h4 className="font-medium text-gray-900 mb-2">{subject || __('(No Subject)')}</h4>
                                    <div className="text-gray-700 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: content || __('(No Content)') }} />
                                    <div className="mt-4 pt-4 border-t text-gray-500" dangerouslySetInnerHTML={{ __html: __('IGWP Team') }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Test Email Section */}
                    <div className="border-t pt-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">{__('Send Test Email')}</h3>
                        <div className="flex items-end space-x-3">
                            <div className="flex-1">
                                <label htmlFor="test-email" className="block text-sm font-medium text-gray-700 mb-1">
                                    {__('Test Email Address')}
                                </label>
                                <input
                                    type="email"
                                    id="test-email"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={__('Enter email address for testing')}
                                    disabled={sendingTest || (emailProgress && emailProgress.in_progress)}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={sendTestEmail}
                                disabled={sendingTest || (emailProgress && emailProgress.in_progress) || !subject.trim() || !content.trim()}
                                className={`
                inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm
                ${sendingTest || (emailProgress && emailProgress.in_progress) || !subject.trim() || !content.trim()
                                        ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                        : 'bg-green-600 hover:bg-green-700 text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
                                    }
            `}
                            >
                                {sendingTest ? (
                                    <>
                                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                                        {__('Sending Test...')}
                                    </>
                                ) : (
                                    <>
                                        <Send className="h-4 w-4 mr-2" />
                                        {__('Send Test')}
                                    </>
                                )}
                            </button>
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                            {__('Send a test email to verify formatting and content before sending to all recipients.')}
                        </p>
                    </div>

                    {/* Send Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={sendCustomEmail}
                            disabled={sending || (emailProgress && emailProgress.in_progress) || !subject.trim() || !content.trim() || (!selectedRecipients.regular && !selectedRecipients.supporter)}
                            className={`
                inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white
                ${sending || (emailProgress && emailProgress.in_progress) || !subject.trim() || !content.trim() || (!selectedRecipients.regular && !selectedRecipients.supporter)
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                }
              `}
                        >
                            {sending || (emailProgress && emailProgress.in_progress) ? (
                                <>
                                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                                    {emailProgress && emailProgress.in_progress ? __('Sending in Progress...') : __('Starting...')}
                                </>
                            ) : (
                                <>
                                    <Send className="h-4 w-4 mr-2" />
                                    {__('Send Email')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Batch Progress Status */}
            {emailProgress && (
                <div className="bg-white shadow rounded-lg">
                    <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                            <Users className="h-5 w-5 text-gray-500" />
                            {emailProgress.in_progress ? __('Email Sending in Progress') : __('Email Sending Complete')}
                        </h3>
                        <button
                            onClick={refreshStatus}
                            className="inline-flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded"
                        >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            {__('Refresh Status')}
                        </button>
                    </div>

                    <div className="px-6 py-4">
                        {/* Progress Overview */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <div className="flex items-center">
                                    <Users className="h-5 w-5 text-blue-500 mr-2" />
                                    <div>
                                        <p className="text-sm font-medium text-blue-800">{__('Total')}</p>
                                        <p className="text-lg font-bold text-blue-900">{emailProgress.total}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-green-50 p-4 rounded-lg">
                                <div className="flex items-center">
                                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                                    <div>
                                        <p className="text-sm font-medium text-green-800">{__('Success')}</p>
                                        <p className="text-lg font-bold text-green-900">{emailProgress.sent}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-red-50 p-4 rounded-lg">
                                <div className="flex items-center">
                                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                                    <div>
                                        <p className="text-sm font-medium text-red-800">{__('Failed')}</p>
                                        <p className="text-lg font-bold text-red-900">{emailProgress.failed}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-yellow-50 p-4 rounded-lg">
                                <div className="flex items-center">
                                    <Clock className="h-5 w-5 text-yellow-500 mr-2" />
                                    <div>
                                        <p className="text-sm font-medium text-yellow-800">{__('Remaining')}</p>
                                        <p className="text-lg font-bold text-yellow-900">{emailProgress.remaining}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-sm font-medium text-gray-700">{__('Progress')}</span>
                                <span className="text-sm text-gray-500">{emailProgress.percent_complete}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${emailProgress.percent_complete}%` }}
                                ></div>
                            </div>
                        </div>

                        {/* Status Information */}
                        <div className="space-y-2 text-sm text-gray-600">
                            {emailProgress.subject && (
                                <p><strong>{__('Subject')}:</strong> {emailProgress.subject}</p>
                            )}

                            {emailProgress.in_progress ? (
                                <>
                                    <p className="flex items-center">
                                        <Loader className="h-4 w-4 mr-2 animate-spin" />
                                        <strong>{__('Status')}:</strong> {__('In progress')}
                                    </p>
                                    {emailProgress.time_elapsed && (
                                        <p><strong>{__('Time elapsed')}:</strong> {emailProgress.time_elapsed}</p>
                                    )}
                                    {emailProgress.estimated_completion && (
                                        <p><strong>{__('Estimated completion')}:</strong> {emailProgress.estimated_completion}</p>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p className="flex items-center text-green-600">
                                        <CheckCircle className="h-4 w-4 mr-2" />
                                        <strong>{__('Status')}:</strong> {__('Completed')}
                                    </p>
                                    {emailProgress.completion_time && (
                                        <p><strong>{__('Total time')}:</strong> {emailProgress.completion_time}</p>
                                    )}
                                </>
                            )}

                            {emailProgress.start_time && (
                                <p><strong>{__('Started')}:</strong> {new Date(emailProgress.start_time).toLocaleString()}</p>
                            )}
                        </div>

                        {/* Recent Activity */}
                        {emailProgress.recent_details && emailProgress.recent_details.length > 0 && (
                            <div className="mt-6">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">{__('Recent Activity')}</h4>
                                <div className="max-h-32 overflow-y-auto bg-gray-50 rounded p-3">
                                    <div className="space-y-1">
                                        {emailProgress.recent_details.slice().reverse().map((detail, index) => (
                                            <div key={index} className="flex items-center justify-between text-xs">
                                                <span className="truncate mr-2">{detail.email}</span>
                                                <div className="flex items-center">
                                                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${detail.status === 'success'
                                                            ? 'bg-green-100 text-green-800'
                                                            : 'bg-red-100 text-red-800'
                                                        }`}>
                                                        {detail.status === 'success' ? __('Success') : __('Failed')}
                                                    </span>
                                                    <span className="ml-2 text-gray-500">
                                                        {new Date(detail.timestamp).toLocaleTimeString()}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Batch Processing Info */}
                        {emailProgress.in_progress && (
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-start">
                                    <Clock className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium">{__('Batch Processing Active')}</p>
                                        <p className="mt-1">
                                            {__('Emails are being sent in batches of 10 every 5 seconds to ensure reliable delivery and avoid overwhelming the email server.')}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomEmailSender;